"use client";
import { Fragment, useState, useMemo } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import Link from "next/link";
import { Hyphenated } from "@/utils/Hyphenated";

export default function CountrySelector({ countries, error }) {
  const [searchQuery, setSearchQuery] = useState("");

  // Sort countries A-Z and filter based on search query
  const filteredCountries = useMemo(() => {
    if (!countries) return [];
    
    // First, sort countries alphabetically by country_name
    const sortedCountries = [...countries].sort((a, b) => {
      const nameA = (a.country_name || "").toLowerCase();
      const nameB = (b.country_name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    // Then filter if there's a search query
    if (!searchQuery.trim()) {
      return sortedCountries;
    }

    const query = searchQuery.toLowerCase().trim();
    return sortedCountries.filter((country) => {
      const countryName = country.country_name?.toLowerCase() || "";
      const alphaCode = country.country_alpha_code?.toLowerCase() || "";
      return countryName.includes(query) || alphaCode.includes(query);
    });
  }, [countries, searchQuery]);

  if (error) return <p>There&apos;s a unexpected error</p>;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className=" inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          Country
          <ChevronDownIcon
            className="-mr-1 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="h-[80vh] overflow-y-scroll absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none will-change-transform">
          {countries ? (
            <div className="py-1">
              {/* Search input */}
              <div className="px-2 mb-2 sticky top-0 bg-white z-10 pt-2">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search country..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#090EDB] focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Countries list */}
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <Menu.Item key={country?.id}>
                    <Link
                      href={`/${Hyphenated(
                        country?.country_name
                      )}`}
                      className="hover:bg-gray-50 text-gray-700 block px-4 py-2 text-sm"
                      onClick={() => setSearchQuery("")}
                    >
                      {country?.country_flag} {country?.country_name}
                    </Link>
                  </Menu.Item>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">
                  No countries found matching &quot;{searchQuery}&quot;
                </div>
              )}
            </div>
          ) : (
            <div className="py-1">
              <Menu.Item>
                <p className="hover:bg-gray-50 text-gray-700 block px-4 py-2 text-sm">
                  Error!
                </p>
              </Menu.Item>
            </div>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
